import React from "react";
import { motion } from "framer-motion";
import { fadeInUp, staggerContainer, staggerItem } from "../utils/animations";

const SkillBar = ({ skill, percentage, color }) => {
  // Define explicit gradient classes for each color
  const getGradientClass = (color) => {
    switch (color) {
      case "primary":
        return "bg-gradient-to-r from-primary to-accent";
      case "accent":
        return "bg-gradient-to-r from-accent to-purple-600";
      case "green-500":
        return "bg-gradient-to-r from-green-500 to-emerald-600";
      default:
        return "bg-gradient-to-r from-primary to-accent";
    }
  };

  const getTextColorClass = (color) => {
    switch (color) {
      case "primary":
        return "text-primary";
      case "accent":
        return "text-accent";
      case "green-500":
        return "text-green-500";
      default:
        return "text-primary";
    }
  };

  return (
    <motion.div className="space-y-2" variants={staggerItem}>
      <div className="flex justify-between text-sm font-medium">
        <span className="text-gray-300">{skill}</span>
        <span className={getTextColorClass(color)}>{percentage}%</span>
      </div>
      <div className="h-2 w-full bg-gray-800 rounded-full overflow-hidden">
        <motion.div
          className={`h-full ${getGradientClass(
            color
          )} rounded-full skill-bar relative overflow-hidden`}
          initial={{ width: 0 }}
          whileInView={{ width: `${percentage}%` }}
          viewport={{ once: true }}
          transition={{ duration: 1.5, ease: "easeOut", delay: 0.2 }}
          data-width={percentage}
        >
          {(skill === "React & Next.js" ||
            skill === "Node.js" ||
            skill === "GitHub") && (
            <motion.div
              className="absolute inset-0 bg-white/20"
              animate={{ x: ["-100%", "100%"] }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            />
          )}
        </motion.div>
      </div>
    </motion.div>
  );
};

const SkillCard = ({ title, icon, color, skills }) => {
  // Define explicit classes for each color
  const getCardClasses = (color) => {
    switch (color) {
      case "primary":
        return {
          border: "hover:border-primary/30",
          iconBg: "bg-primary/20",
          iconText: "text-primary",
        };
      case "accent":
        return {
          border: "hover:border-accent/30",
          iconBg: "bg-accent/20",
          iconText: "text-accent",
        };
      case "green-500":
        return {
          border: "hover:border-green-500/30",
          iconBg: "bg-green-500/20",
          iconText: "text-green-500",
        };
      default:
        return {
          border: "hover:border-primary/30",
          iconBg: "bg-primary/20",
          iconText: "text-primary",
        };
    }
  };

  const classes = getCardClasses(color);

  return (
    <motion.div
      className={`bg-card-bg/50 border border-white/10 rounded-[2rem] p-8 ${classes.border} transition-colors group hover-card`}
      variants={fadeInUp}
      whileHover={{ y: -10, scale: 1.02 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <div className="flex items-center gap-4 mb-8">
        <motion.div
          className={`w-12 h-12 rounded-2xl ${classes.iconBg} flex items-center justify-center ${classes.iconText} group-hover:scale-110 transition-transform`}
          whileHover={{ rotate: 360 }}
          transition={{ duration: 0.5 }}
        >
          <span className="material-symbols-outlined text-2xl">{icon}</span>
        </motion.div>
        <h3 className="text-2xl font-bold font-display text-white">{title}</h3>
      </div>
      <motion.div
        className="space-y-6"
        variants={staggerContainer}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
      >
        {skills.map((skill, index) => (
          <SkillBar
            key={index}
            skill={skill.name}
            percentage={skill.percentage}
            color={color}
          />
        ))}
      </motion.div>
    </motion.div>
  );
};

const SkillsSection = () => {
  const skillCategories = [
    {
      title: "Frontend",
      icon: "web",
      color: "primary",
      skills: [
        { name: "React & Next.js", percentage: 75 },
        { name: "Tailwind CSS", percentage: 90 },
        { name: "JavaScript", percentage: 85 },
        { name: "Framer Motion", percentage: 80 },
      ],
    },
    {
      title: "Backend",
      icon: "dns",
      color: "accent",
      skills: [
        { name: "Node.js", percentage: 70 },
        { name: "Express.js", percentage: 75 },
        { name: "MongoDB", percentage: 80 },
      ],
    },
    {
      title: "Tools & Cloud",
      icon: "build",
      color: "green-500",
      skills: [
        { name: "GitHub", percentage: 85 },
        { name: "Postman/Thunder Client", percentage: 82 },
        { name: "Figma", percentage: 90 },
      ],
    },
  ];

  return (
    <motion.section
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full mt-32"
      initial="initial"
      whileInView="animate"
      viewport={{ once: true, amount: 0.3 }}
      variants={staggerContainer}
    >
      <motion.div
        className="flex items-end justify-between mb-10"
        variants={fadeInUp}
      >
        <div>
          <h2 className="text-3xl font-display font-bold text-white text-reveal">
            Selected Skills
          </h2>
          <p className="text-gray-400 mt-2">Tools I use to push boundaries.</p>
        </div>
        <div className="hidden sm:block h-px flex-1 bg-white/10 ml-8 mb-4"></div>
      </motion.div>
      <motion.div
        className="grid grid-cols-1 md:grid-cols-3 gap-8"
        variants={staggerContainer}
      >
        {skillCategories.map((category, index) => (
          <SkillCard
            key={index}
            title={category.title}
            icon={category.icon}
            color={category.color}
            skills={category.skills}
          />
        ))}
      </motion.div>
    </motion.section>
  );
};

export default SkillsSection;
